// Each web font is followed by its size-matched local fallback (the
// '... Fallback' faces in design.css), so text does not jump when the real
// font arrives
export const FONT_FAMILY_MAP = {
  default: "Karla, 'Karla Fallback', Helvetica, sans-serif",
  inter: "'Inter', 'Inter Fallback', sans-serif",
  poppins: "'Poppins', 'Poppins Fallback', sans-serif",
  playfair: "'Playfair Display', 'Playfair Display Fallback', serif",
  'roboto-mono': "'Roboto Mono', 'Roboto Mono Fallback', monospace",
  pacifico: "'Pacifico', cursive"
};

// The keys come from user data; a plain MAP[key] would also find
// Object.prototype's members ('constructor' and friends)
const own = (map, key) => Object.prototype.hasOwnProperty.call(map, key);

const RADIUS_MAP = {
  square: '0px',
  rounded: '10px',
  pill: '999px'
};

export const DEFAULT_DESIGN = {
  backgroundType: 'solid',
  backgroundColor: '#6160ab',
  gradientFrom: '#eaafc8',
  gradientTo: '#654ea3',
  backgroundImage: '',
  backgroundOverlay: 30,
  buttonStyle: 'filled',
  buttonShape: 'square',
  buttonColor: '#1f1c1c',
  buttonTextColor: '#6160ab',
  textColor: '#1f1c1c',
  font: 'default'
};

// The server only ever stores names it generated (uploads/bg-<time>-<uuid>.jpg),
// but this string lands inside url(...), so anything else is ignored outright.
const SAFE_UPLOAD = /^uploads\/[\w.-]+$/;

function backgroundFor(d) {
  if (d.backgroundType === 'image' && SAFE_UPLOAD.test(d.backgroundImage || '')) {
    const shade = Math.min(Math.max(Number(d.backgroundOverlay) || 0, 0), 80) / 100;
    // The solid colour shows while the photo loads
    return `linear-gradient(rgba(0,0,0,${shade}), rgba(0,0,0,${shade})), url("/${d.backgroundImage}") center / cover no-repeat, ${d.backgroundColor}`;
  }
  if (d.backgroundType === 'gradient') {
    return `linear-gradient(to bottom, ${d.gradientFrom}, ${d.gradientTo})`;
  }
  return d.backgroundColor;
}

// Computes the CSS custom properties + class names that render a user's
// `design` object. Shared by the real public profile (ProfileView.js) and the
// dashboard's live preview so the two never drift apart. src/profileView.js
// on the server computes the same for the first paint; change both together.
export function getProfileStyle(design) {
  const d = { ...DEFAULT_DESIGN, ...design };

  const style = {
    '--profile-bg': backgroundFor(d),
    '--btn-bg': d.buttonColor,
    '--btn-text': d.buttonTextColor,
    '--text-color': d.textColor,
    '--btn-radius': own(RADIUS_MAP, d.buttonShape) ? RADIUS_MAP[d.buttonShape] : RADIUS_MAP.square,
    fontFamily: own(FONT_FAMILY_MAP, d.font) ? FONT_FAMILY_MAP[d.font] : FONT_FAMILY_MAP.default
  };

  const className = `profile-page button-${d.buttonStyle}`;

  return { style, className };
}

// Flat colours standing in for a gradient where one cannot go. The page
// behind an overscroll takes the gradient's end: on phones the bounce people
// see is at the bottom of the list (Android has no top bounce at all). The
// browser's toolbar sits at the top, so theme-color takes its start.
// Same as pageColor/themeColor in src/profileView.js.
export function pageColor(design) {
  const d = { ...DEFAULT_DESIGN, ...design };
  return d.backgroundType === 'gradient' ? d.gradientTo : d.backgroundColor;
}

export function themeColor(design) {
  const d = { ...DEFAULT_DESIGN, ...design };
  return d.backgroundType === 'gradient' ? d.gradientFrom : d.backgroundColor;
}
